export type HeadingOutlineItem = {
  level: 2 | 3
  text: string
  id: string
}

/** Strip common inline Markdown from a heading line for display & slugging. */
export function stripInlineMd(raw: string): string {
  return raw
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/`(.+?)`/g, '$1')
    .replace(/\[(.+?)\]\(.+?\)/g, '$1')
    .trim()
}

export function slugifyHeading(text: string): string {
  const s = text
    .toLowerCase()
    .trim()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return s || 'section'
}

/**
 * Build ordered outline (h2, h3 only) with GitHub-style unique ids.
 */
export function buildHeadingOutline(markdown: string): HeadingOutlineItem[] {
  const lines = markdown.split(/\n/)
  const slugCounts = new Map<string, number>()
  const outline: HeadingOutlineItem[] = []

  const allocId = (text: string): string => {
    const base = slugifyHeading(text)
    const n = slugCounts.get(base) ?? 0
    slugCounts.set(base, n + 1)
    return n === 0 ? base : `${base}-${n}`
  }

  for (const line of lines) {
    const h3 = line.match(/^###\s+(.+)$/)
    const h2 = line.match(/^##\s+(.+)$/)
    if (h3) {
      const text = stripInlineMd(h3[1].trim())
      if (text) outline.push({ level: 3, text, id: allocId(text) })
    } else if (h2) {
      const text = stripInlineMd(h2[1].trim())
      if (text) outline.push({ level: 2, text, id: allocId(text) })
    }
  }

  return outline
}
